import { geoCentroid } from "d3-geo";
import type { NeighborhoodCollection, PhillyProfile, Rating, Ratings } from "./types";

interface RatedPoint {
  name: string;
  rating: Rating;
  lon: number;
  lat: number;
}

interface SignalSet {
  completion: number;
  ratedCount: number;
  mean: number;
  positiveShare: number;
  neverShare: number;
  maybeShare: number;
  dispersion: number;
  neighborDifference: number;
  regions: Record<RegionKey, number>;
  regionCounts: Record<RegionKey, number>;
  regionPositiveCounts: Record<RegionKey, number>;
  positiveCount: number;
  broadPositiveZones: number;
}

type RegionKey = "center" | "centerWest" | "centerEast" | "centerSouth" | "centerNorth" | "south" | "west" | "northwest" | "northeast" | "riverwards" | "broad";

const CENTER_SUBREGIONS: RegionKey[] = ["centerWest", "centerEast", "centerSouth", "centerNorth"];
const BROAD_REGIONS: RegionKey[] = ["center", "south", "west", "northwest", "northeast", "riverwards", "broad"];

const NAMED_REGIONS: Partial<Record<RegionKey, Set<string>>> = {
  centerWest: new Set(["Rittenhouse", "Fitler Square", "Logan Square", "Graduate Hospital"]),
  centerEast: new Set(["Center City East", "Washington Square West", "Society Hill", "Old City", "Chinatown", "Queen Village", "Bella Vista", "Hawthorne"]),
  centerSouth: new Set(["Point Breeze", "Newbold", "Passyunk Square", "East Passyunk", "Pennsport", "Dickinson Narrows", "Lower Moyamensing", "West Passyunk"]),
  centerNorth: new Set(["Fairmount", "Spring Garden", "Francisville", "Callowhill", "Northern Liberties", "East Poplar", "West Poplar", "Ludlow", "Yorktown"]),
  riverwards: new Set(["Fishtown - Lower Kensington", "East Kensington", "Kensington, Old", "Upper Kensington", "Port Richmond", "Richmond"]),
};

const REGION_COPY: Record<RegionKey, { name: string; description: string; eyebrow: string }> = {
  center: {
    name: "The Greater Center City Orbit",
    eyebrow: "A compact Philly radius",
    description: "Your map stays close to the dense middle of the city: walkable blocks, frequent transit, and most of daily life within a few neighborhoods.",
  },
  centerWest: {
    name: "The Schuylkill-Side Walker",
    eyebrow: "Center City, westbound",
    description: "Your map leans toward the Center City blocks nearest the river: walkable, transit-rich, and never too far from the trail.",
  },
  centerEast: {
    name: "The East-of-Broad Stroller",
    eyebrow: "Old streets, short walks",
    description: "You favor the denser east side of Greater Center City: small blocks, old brick, restaurants, and errands that rarely require a car.",
  },
  centerSouth: {
    name: "The South Philly Rowhouse Belt",
    eyebrow: "South of South, more or less",
    description: "Your choices trace the rowhouse neighborhoods below Center City, where the blocks are tight, the food opinions are strong, and Broad Street is never far.",
  },
  centerNorth: {
    name: "The North-of-Vine Orbit",
    eyebrow: "Center City, one neighborhood north",
    description: "You want downtown within reach but prefer the neighborhoods just above it: rowhouses, new corners, and an easy trip back toward City Hall.",
  },
  south: {
    name: "The South Philly Parking Chair",
    eyebrow: "Rowhouse range",
    description: "Your ideal Philly has tight blocks, strong food opinions, stadium lights, and a very specific feeling about parking.",
  },
  west: {
    name: "The West Philly Porch Person",
    eyebrow: "Trolley territory",
    description: "Give you a porch, a tree-lined block, and a trolley that eventually shows up and you are set.",
  },
  northwest: {
    name: "The Wissahickon Escapist",
    eyebrow: "Creek-side preferences",
    description: "You want city life with trees, stone walls, and a trail out the back door.",
  },
  northeast: {
    name: "The Boulevard Regular",
    eyebrow: "Northeast range",
    description: "You favor long avenues, deep neighborhood roots, and a little more room to spread out.",
  },
  riverwards: {
    name: "The El-and-River Regular",
    eyebrow: "River Wards range",
    description: "Your favorites follow the El and the Delaware, with corner stores, rowhouses, and strong opinions about exactly where one neighborhood becomes the next.",
  },
  broad: {
    name: "The Broad Street Line Loyalist",
    eyebrow: "One line, no nonsense",
    description: "Your Philly runs north to south: home should be an easy walk to Broad, the ride should be direct, and transfers are best treated as a personal insult.",
  },
};

export function buildProfile(ratings: Ratings, neighborhoods: NeighborhoodCollection): PhillyProfile {
  const signals = calculateSignals(ratings, neighborhoods);
  const badges = buildBadges(signals);

  if (signals.ratedCount < 8) {
    return {
      name: "The Map Is Just Getting Started",
      eyebrow: "Too soon to tell",
      description: "A few more neighborhoods will make your Philly type much less mysterious.",
      reason: `${signals.ratedCount} neighborhoods are rated so far.`,
      confidence: "early read",
      badges,
    };
  }

  if (signals.maybeShare >= 0.38) {
    return profile(
      "The Maybe Maven",
      "Keeping the options open",
      "You can imagine a life in a lot of places, but you would like to walk the block first.",
      `${Math.round(signals.maybeShare * 100)}% of your map is a maybe.`,
      signals,
      badges,
    );
  }

  if (signals.positiveShare >= 0.7 && signals.dispersion >= 0.48) {
    return profile(
      "The Anywhere-in-Philly Optimist",
      "Citywide range",
      "Your favorites stretch from the Delaware to the county line. You can picture a version of home almost anywhere in Philly.",
      `${Math.round(signals.positiveShare * 100)}% of your ratings are yes or absolutely.`,
      signals,
      badges,
    );
  }

  const strongestRegion = regionalLead(signals);
  if (strongestRegion) {
    const copy = REGION_COPY[strongestRegion.key];
    const reason = strongestRegion.margin >= 0.58
      ? `${formatRegion(strongestRegion.key)} runs ${strongestRegion.margin.toFixed(1)} points above your citywide average.`
      : `${Math.round(strongestRegion.concentration * 100)}% of your yes and absolutely ratings land in ${formatRegion(strongestRegion.key)}.`;
    return profile(
      copy.name,
      copy.eyebrow,
      copy.description,
      reason,
      signals,
      badges,
    );
  }

  if (signals.dispersion < 0.22 && signals.positiveShare >= 0.2) {
    return profile(
      "The Few-Block Devotee",
      "Keeping it close",
      "You do not need the whole city. You need your few square blocks and the places you already know by heart.",
      "Your favorite neighborhoods form the tightest geographic cluster on your map.",
      signals,
      badges,
    );
  }

  if (signals.neighborDifference >= 0.48) {
    return profile(
      "The Block-by-Block Contrarian",
      "Every block is its own case",
      "Your preferences change fast from one neighborhood to the next. Proximity earns no free passes.",
      "Nearby neighborhoods receive unusually different ratings on your map.",
      signals,
      badges,
    );
  }

  if (signals.neverShare >= 0.56) {
    return profile(
      "The Hard-to-Move Philadelphian",
      "The lease remains unsigned",
      "Most of the city has not cleared your bar yet. You are waiting for the right block, the right commute, and a rowhouse inspection without a surprise.",
      `${Math.round(signals.neverShare * 100)}% of your ratings are never, without one favorite part of town taking over the map.`,
      signals,
      badges,
    );
  }

  return profile(
    "The Neighborhood-by-Neighborhood Type",
    "No simple pattern",
    "No one part of Philly owns your map. Every neighborhood gets considered on its own, preferably over a soft pretzel.",
    "Your ratings are balanced across the city without one dominant area.",
    signals,
    badges,
  );
}

function calculateSignals(ratings: Ratings, neighborhoods: NeighborhoodCollection): SignalSet {
  const points: RatedPoint[] = [];
  for (const feature of neighborhoods.features) {
    const rating = ratings[String(feature.properties.id)];
    if (rating === undefined) continue;
    const [lon, lat] = geoCentroid(feature);
    points.push({ name: feature.properties.name, rating, lon, lat });
  }

  const count = points.length;
  const mean = count ? average(points.map((point) => point.rating)) : 0;
  const positivePoints = points.filter((point) => point.rating >= 3);
  const regions = emptyRegionRecord();
  const regionCounts = emptyRegionRecord();
  const regionPositiveCounts = emptyRegionRecord();
  const broadPositiveZones = new Set<BroadZone>();

  for (const point of points) {
    const memberships = regionsFor(point.name, point.lon, point.lat);
    for (const region of memberships) {
      regions[region] += point.rating;
      regionCounts[region] += 1;
      if (point.rating >= 3) regionPositiveCounts[region] += 1;
    }
    if (point.rating >= 3 && memberships.includes("broad")) broadPositiveZones.add(broadZoneFor(point.lat));
  }
  for (const region of Object.keys(regions) as RegionKey[]) {
    regions[region] = regionCounts[region] ? regions[region] / regionCounts[region] : mean;
  }

  return {
    completion: count / neighborhoods.features.length,
    ratedCount: count,
    mean,
    positiveShare: count ? positivePoints.length / count : 0,
    neverShare: count ? points.filter((point) => point.rating === 0).length / count : 0,
    maybeShare: count ? points.filter((point) => point.rating === 2).length / count : 0,
    dispersion: geographicDispersion(positivePoints),
    neighborDifference: localDifference(points),
    regions,
    regionCounts,
    regionPositiveCounts,
    positiveCount: positivePoints.length,
    broadPositiveZones: broadPositiveZones.size,
  };
}

function regionsFor(name: string, lon: number, lat: number): RegionKey[] {
  const regions: RegionKey[] = [];
  if (lat >= 39.935 && lat <= 39.985 && lon >= -75.205 && lon <= -75.125) regions.push("center");
  if (lat < 39.95 && lon > -75.27) regions.push("south");
  if (lon < -75.19 && lat >= 39.93 && lat < 40.015) regions.push("west");
  if (lon < -75.17 && lat >= 40.015) regions.push("northwest");
  if (lon >= -75.17 && lat >= 40.0) regions.push("northeast");
  if (Math.abs(lon - -75.1652) < 0.017) regions.push("broad");
  for (const [region, names] of Object.entries(NAMED_REGIONS) as [RegionKey, Set<string>][]) {
    if (names.has(name)) regions.push(region);
  }
  return regions;
}

type BroadZone = "north" | "center" | "south";

function broadZoneFor(lat: number): BroadZone {
  if (lat >= 39.985) return "north";
  if (lat < 39.935) return "south";
  return "center";
}

function geographicDispersion(points: RatedPoint[]): number {
  if (points.length < 2) return 0;
  const centerLon = average(points.map((point) => point.lon));
  const centerLat = average(points.map((point) => point.lat));
  const distances = points.map((point) => Math.hypot((point.lon - centerLon) * 0.77, point.lat - centerLat));
  return Math.min(1, average(distances) / 0.105);
}

function localDifference(points: RatedPoint[]): number {
  if (points.length < 4) return 0;
  const differences = points.map((point) => {
    const neighbors = points
      .filter((candidate) => candidate !== point)
      .map((candidate) => ({ candidate, distance: Math.hypot(candidate.lon - point.lon, candidate.lat - point.lat) }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 3);
    return average(neighbors.map(({ candidate }) => Math.abs(candidate.rating - point.rating) / 4));
  });
  return average(differences);
}

function regionalLead(signals: SignalSet): { key: RegionKey; margin: number; concentration: number } | null {
  const candidate = (keys: RegionKey[]) => keys
    .filter((key) => signals.regionCounts[key] >= 3 && signals.regionPositiveCounts[key] >= 3)
    .filter((key) => key !== "broad" || signals.broadPositiveZones === 3)
    .map((key) => ({
      key,
      margin: signals.regions[key] - signals.mean,
      concentration: signals.positiveCount ? signals.regionPositiveCounts[key] / signals.positiveCount : 0,
    }))
    .filter(({ margin, concentration }) => margin >= 0.58 || concentration >= 0.58)
    .sort((a, b) => (b.concentration + Math.max(0, b.margin) / 4) - (a.concentration + Math.max(0, a.margin) / 4))[0] ?? null;

  const specific = candidate(CENTER_SUBREGIONS);
  if (specific && specific.concentration >= 0.55) {
    const centerPreference = signals.regions.center;
    const isDistinctWithinCenter = specific.concentration >= 0.72 || specific.margin - (centerPreference - signals.mean) >= 0.35;
    if (isDistinctWithinCenter) return specific;
  }
  return candidate(BROAD_REGIONS);
}

function buildBadges(signals: SignalSet): PhillyProfile["badges"] {
  const badges: PhillyProfile["badges"] = [];
  badges.push({
    label: signals.completion === 1 ? "Map complete" : `${Math.round(signals.completion * 100)}% mapped`,
    detail: signals.completion === 1 ? "All 159 neighborhoods rated" : `${signals.ratedCount} neighborhoods rated`,
  });

  if (signals.positiveShare >= 0.6 && signals.completion >= 0.5) badges.push({ label: "Big yes map", detail: "Most of Philadelphia gets a warm welcome" });
  else if (signals.positiveShare >= 0.6) badges.push({ label: "Warm start", detail: "Most neighborhoods rated so far get a yes" });
  else if (signals.neverShare >= 0.45) badges.push({ label: "Hard to impress", detail: "Your list of must-haves is doing serious work" });
  else badges.push({ label: "Mixed map", detail: "Yes, no, and maybe all make an appearance" });

  if (signals.maybeShare <= 0.1 && signals.ratedCount >= 10) badges.push({ label: "Decisive", detail: "You rarely reach for maybe" });
  else if (signals.maybeShare >= 0.3) badges.push({ label: "Maybe pile", detail: "A lot of neighborhoods need another look" });

  const region = regionalLead(signals);
  if (region) badges.push({ label: `${formatRegion(region.key)} favorite`, detail: "Your strongest part of the map" });
  else if (signals.dispersion >= 0.5 && signals.positiveShare > 0.2) badges.push({ label: "Cross-town favorites", detail: "Your favorites span the city" });

  return badges.slice(0, 4);
}

function profile(
  name: string,
  eyebrow: string,
  description: string,
  reason: string,
  signals: SignalSet,
  badges: PhillyProfile["badges"],
): PhillyProfile {
  const confidence = signals.completion >= 0.85 ? "strong read" : signals.completion >= 0.35 ? "taking shape" : "early read";
  return { name, eyebrow, description, reason, confidence, badges };
}

function emptyRegionRecord(): Record<RegionKey, number> {
  return { center: 0, centerWest: 0, centerEast: 0, centerSouth: 0, centerNorth: 0, south: 0, west: 0, northwest: 0, northeast: 0, riverwards: 0, broad: 0 };
}

function formatRegion(region: RegionKey): string {
  return {
    center: "Center City",
    centerWest: "the Schuylkill side of Center City",
    centerEast: "east of Broad",
    centerSouth: "the South Philly rowhouse belt",
    centerNorth: "the neighborhoods north of Vine",
    south: "South Philly",
    west: "West Philly",
    northwest: "Northwest",
    northeast: "Northeast",
    riverwards: "the River Wards",
    broad: "Broad Street",
  }[region];
}

function average(values: number[]): number {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
}
